import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  DragStartEvent,
  DragEndEvent,
  pointerWithin,
} from '@dnd-kit/core';
import { FiArrowLeft, FiEdit2, FiCheck, FiX } from 'react-icons/fi';
import { networkManager, PlayerShipData } from '../../systems/NetworkManager';
import { emitGameEvent } from '../../core/GameEvents';
import type { InventorySlotData, ShipInventoryItem, InventoryData } from '../../systems/NetworkManager';
import { SHIPS, ShipClass, ShipClassType, getEquipmentById, canEquipInSlot, RESOURCES } from '@astra/shared';
import { SHIP_DESIGNS, SHIP_ID_TO_DESIGN, SIZE_SCALE_MULTIPLIERS } from '../../entities/ShipGraphics';
import {
  PALETTE, SLOT_COLORS, RESOURCE_COLORS, getTierColor as getTierColorShared,
  stationContainerStyle, stationHeaderStyle, stationBackButtonStyle,
  stationHeaderTabsStyle, stationHeaderTabStyle, stationHintStyle, stationFooterStyle,
} from './stationTheme';

// Ship class filter options
const SHIP_CLASSES: Array<{ id: ShipClassType | 'all'; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'corvette', label: 'Corvette' },
  { id: 'frigate', label: 'Frigate' },
  { id: 'destroyer', label: 'Destroyer' },
  { id: 'cruiser', label: 'Cruiser' },
  { id: 'battleship', label: 'Battleship' },
  { id: 'carrier', label: 'Carrier' },
  { id: 'dreadnought', label: 'Dreadnought' },
  { id: 'hauler', label: 'Hauler' },
];

interface ShipyardProps {
  stationId: string;
  onBack: () => void;
}

// Helper functions
function getShipDisplayName(ship: PlayerShipData, shipClass: ShipClass | undefined): string {
  if (ship.customName) return ship.customName;
  return shipClass?.name || ship.shipClassId;
}

const getTierColor = getTierColorShared;

function getClassDisplayName(classType: string): string {
  return classType.charAt(0).toUpperCase() + classType.slice(1);
}

// Ship Preview Component - renders ship using SVG file or fallback to points
function ShipPreview({
  shipClassId,
  size = 'medium',
  color = PALETTE.TEAL,
  previewSize = 120,
}: {
  shipClassId: string;
  size?: 'small' | 'medium' | 'large';
  color?: string;
  previewSize?: number;
}) {
  const designKey = SHIP_ID_TO_DESIGN[shipClassId] || shipClassId;
  const svgUrl = `/assets/ships/${designKey}.svg`;

  // Try to use SVG file first (rotated -90° since SVGs point right)
  const [useSvg, setUseSvg] = useState(true);

  // Fallback to point-based rendering
  const design = SHIP_DESIGNS[designKey] || SHIP_DESIGNS.corvette;
  const sizeMultiplier = SIZE_SCALE_MULTIPLIERS[size] || 1.0;
  const totalScale = design.scale * sizeMultiplier;

  // Calculate bounds for point-based fallback
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const p of design.points) {
    minX = Math.min(minX, p.x * totalScale);
    maxX = Math.max(maxX, p.x * totalScale);
    minY = Math.min(minY, p.y * totalScale);
    maxY = Math.max(maxY, p.y * totalScale);
  }
  const shipWidth = maxX - minX;
  const shipHeight = maxY - minY;
  const padding = 20;
  const availableSize = previewSize - padding * 2;
  const fitScale = Math.min(availableSize / shipWidth, availableSize / shipHeight);

  // Build SVG path for fallback
  const pathPoints = design.points.map((p, i) => {
    const x = p.x * totalScale * fitScale + previewSize / 2;
    const y = p.y * totalScale * fitScale + previewSize / 2;
    return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
  }).join(' ') + ' Z';

  return (
    <div
      style={{
        width: previewSize,
        height: previewSize,
        backgroundColor: '#000',
        border: `1px solid ${PALETTE.BORDER}`,
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        overflow: 'hidden',
      }}
    >
      {useSvg ? (
        <img
          src={svgUrl}
          alt={designKey}
          style={{
            width: previewSize * 0.8,
            height: previewSize * 0.8,
            objectFit: 'contain',
            transform: 'rotate(-90deg)', // SVGs point right, rotate to point up
          }}
          onError={() => setUseSvg(false)}
        />
      ) : (
        <svg width={previewSize} height={previewSize} viewBox={`0 0 ${previewSize} ${previewSize}`}>
          <path
            d={pathPoints}
            fill="none"
            stroke={color}
            strokeWidth="2"
          />
        </svg>
      )}
    </div>
  );
}

// Ship Info Box Component - shows ship preview with stats
function ShipInfoBox({
  ship,
  shipClass,
  title,
  color = PALETTE.TEAL,
  showPower = true,
}: {
  ship?: PlayerShipData;
  shipClass?: ShipClass;
  title: string;
  color?: string;
  showPower?: boolean;
}) {
  if (!shipClass) return null;

  return (
    <div
      style={{
        display: 'flex',
        gap: '16px',
        padding: '12px',
        backgroundColor: PALETTE.PANEL,
        border: `1px solid ${PALETTE.BORDER}`,
        borderRadius: '4px',
        marginBottom: '12px',
      }}
    >
      <ShipPreview
        shipClassId={shipClass.id}
        size={shipClass.size}
        color={color}
        previewSize={100}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: PALETTE.DIM, fontSize: '9px', marginBottom: '4px', textTransform: 'uppercase' }}>
          {title}
        </div>
        <div style={{ color: PALETTE.LIGHT, fontWeight: 600, fontSize: '11px', marginBottom: '6px' }}>
          {ship?.customName || shipClass.name}
        </div>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '11px', color: PALETTE.DIM }}>
          <span>
            <span style={{ color: getTierColor(shipClass.tier) }}>T{shipClass.tier}</span>
            {' '}{getClassDisplayName(shipClass.class)}
          </span>
          <span>
            Size: <span style={{ color: PALETTE.LIGHT }}>{shipClass.size.charAt(0).toUpperCase() + shipClass.size.slice(1)}</span>
          </span>
          {showPower && (
            <span>
              Fitting: <span style={{ color: PALETTE.ORANGE }}>{shipClass.stats.powerCapacity}</span>
            </span>
          )}
        </div>
        {ship && (
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '11px', color: PALETTE.DIM, marginTop: '4px' }}>
            <span>
              Hull: <span style={{ color: ship.hull < shipClass.stats.hull * 0.3 ? PALETTE.RED : PALETTE.GREEN }}>
                {Math.round(ship.hull)}/{shipClass.stats.hull}
              </span>
            </span>
            <span>
              Cargo: <span style={{ color: PALETTE.LIGHT }}>{shipClass.stats.cargoCapacity}</span>
            </span>
            <span>
              Slots: <span style={{ color: PALETTE.LIGHT }}>{shipClass.slots.length}</span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}


// Draggable item component
function DraggableItem({
  id,
  children,
  data,
  disabled,
}: {
  id: string;
  children: React.ReactNode;
  data: { type: 'equipped' | 'inventory'; slotIndex?: number; equipmentId?: string };
  disabled?: boolean;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    data,
    disabled,
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'grab',
        touchAction: 'none',
      }}
    >
      {children}
    </div>
  );
}

// Droppable slot component
function DroppableSlot({
  id,
  children,
  data,
  canDrop,
}: {
  id: string;
  children: React.ReactNode;
  data: { slotType: string; slotIndex: number; size: string };
  canDrop?: boolean;
}) {
  const { setNodeRef, isOver: dropping } = useDroppable({
    id,
    data,
  });

  const showDropIndicator = dropping && canDrop;

  return (
    <div
      ref={setNodeRef}
      style={{
        border: showDropIndicator ? `2px solid ${PALETTE.GREEN}` : undefined,
        backgroundColor: showDropIndicator ? `${PALETTE.GREEN}22` : undefined,
        borderRadius: '4px',
        transition: 'all 0.15s ease',
      }}
    >
      {children}
    </div>
  );
}

// Inventory drop zone component
function InventoryDropZone({ id, children }: { id: string; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      style={{
        flex: 1,
        backgroundColor: isOver ? `${PALETTE.TEAL}11` : 'transparent',
        borderRadius: '4px',
        transition: 'background-color 0.15s ease',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {children}
    </div>
  );
}

// Bank drop zone component
function BankDropZone({ id, isDragging }: { id: string; isDragging: boolean }) {
  const { setNodeRef, isOver } = useDroppable({ id });

  if (!isDragging) return null;

  return (
    <div
      ref={setNodeRef}
      style={{
        margin: '8px 16px',
        padding: '14px',
        border: `2px dashed ${isOver ? PALETTE.YELLOW : PALETTE.DIM}66`,
        borderRadius: '6px',
        backgroundColor: isOver ? `${PALETTE.YELLOW}15` : 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        transition: 'all 0.15s ease',
      }}
    >
      <span style={{
        fontSize: '11px',
        color: isOver ? PALETTE.YELLOW : PALETTE.DIM,
      }}>
        &#x1F4E6;
      </span>
      <span style={{
        fontSize: '11px',
        color: isOver ? PALETTE.YELLOW : PALETTE.DIM,
        fontWeight: isOver ? 'bold' : 'normal',
        textTransform: 'uppercase',
        letterSpacing: '1px',
      }}>
        {isOver ? 'Drop to store' : 'Send to Bank'}
      </span>
    </div>
  );
}

export function Shipyard({ stationId, onBack }: ShipyardProps) {
  // Tab state
  const [activeTab, setActiveTab] = useState<'ships' | 'equipment'>('equipment');

  // Ships data
  const [ships, setShips] = useState<PlayerShipData[]>([]);
  const [currentShipId, setCurrentShipId] = useState<string>('');
  const [selectedShipId, setSelectedShipId] = useState<string>('');
  const [shipsLoading, setShipsLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [shipError, setShipError] = useState<string | null>(null);
  const [classFilter, setClassFilter] = useState<ShipClassType | 'all'>('all');

  // Rename state
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');

  // Equipment/inventory data
  const [slots, setSlots] = useState<InventorySlotData[]>([]);
  const [shipInventory, setShipInventory] = useState<ShipInventoryItem[]>([]);
  const [shipClassId, setShipClassId] = useState('corvette_t1');
  const [equipmentLoading, setEquipmentLoading] = useState(false);
  const [activeData, setActiveData] = useState<any>(null);

  // Calculate cargo capacity
  const shipClass = SHIPS[shipClassId];
  const baseCargoCapacity = shipClass?.stats.cargoCapacity || 10;

  // Calculate bonus from cargo expanders
  let cargoBonus = 0;
  for (const slot of slots) {
    if (slot.equipped) {
      const equipment = getEquipmentById(slot.equipped);
      if (equipment && 'enhancerType' in equipment && (equipment as any).enhancerType === 'cargo_expander') {
        cargoBonus += (equipment as any).stats?.cargoBonus || 0;
      }
    }
  }
  const cargoCapacity = baseCargoCapacity + cargoBonus;

  // Calculate cargo used
  let cargoUsed = 0;
  for (const item of shipInventory) {
    if (item.itemType === 'resource') {
      cargoUsed += item.quantity;
    } else if (item.itemType === 'equipment') {
      const eq = getEquipmentById(item.itemId);
      cargoUsed += (eq?.volume || 1) * item.quantity;
    }
  }

  // Count equipped items
  const equippedCount = slots.filter(s => s.equipped).length;
  const hasEquippedItems = equippedCount > 0;

  // Request ship data on mount
  useEffect(() => {
    networkManager.requestPlayerShips(stationId);
    setShipsLoading(true);
  }, [stationId]);

  // Listen for ship data
  useEffect(() => {
    const prevHandler = networkManager.onPlayerShips;

    networkManager.onPlayerShips = (data) => {
      setShips(data.ships);
      setCurrentShipId(data.currentShipId);
      setShipsLoading(false);

      // Select first non-active ship by default
      const otherShips = data.ships.filter(s => s.id !== data.currentShipId);
      if (!selectedShipId || selectedShipId === data.currentShipId || !otherShips.find(s => s.id === selectedShipId)) {
        setSelectedShipId(otherShips.length > 0 ? otherShips[0].id : '');
      }
    };

    return () => {
      networkManager.onPlayerShips = prevHandler;
    };
  }, [selectedShipId]);

  // Listen for ship switch result
  useEffect(() => {
    const prevHandler = networkManager.onShipSwitchResult;

    networkManager.onShipSwitchResult = (data) => {
      setSwitching(false);
      if (data.success && data.newShipId) {
        setCurrentShipId(data.newShipId);
        setSelectedShipId('');
        setShipError(null);
        networkManager.requestPlayerShips(stationId);
        // Also refresh inventory since ship changed
        networkManager.requestInventoryData('');
        emitGameEvent('notification', { type: 'success', message: 'Ship switched successfully!' });
      } else {
        setShipError(data.error || 'Failed to switch ships');
      }
    };

    return () => {
      networkManager.onShipSwitchResult = prevHandler;
    };
  }, [stationId]);

  // Listen for ship rename result
  useEffect(() => {
    const prevHandler = networkManager.onShipRenameResult;

    networkManager.onShipRenameResult = (data) => {
      if (data.success) {
        setIsRenaming(false);
        emitGameEvent('notification', { type: 'success', message: 'Ship renamed!' });
        networkManager.requestPlayerShips(stationId);
      } else {
        emitGameEvent('notification', { type: 'error', message: data.error || 'Failed to rename ship' });
      }
    };

    return () => {
      networkManager.onShipRenameResult = prevHandler;
    };
  }, [stationId]);

  // Load inventory data
  const loadInventory = useCallback(() => {
    setEquipmentLoading(true);
    networkManager.requestInventoryData('');
  }, []);

  // Handle inventory data from server
  useEffect(() => {
    const handleInventoryData = (data: InventoryData) => {
      setSlots(data.slots || []);
      setShipInventory(data.shipInventory || []);
      setShipClassId(data.shipClassId);
      setEquipmentLoading(false);
    };

    networkManager.onInventoryData = handleInventoryData;

    networkManager.onEquipResult = (data) => {
      if (data.success) {
        loadInventory();
        emitGameEvent('notification', { type: 'success', message: 'Item equipped' });
      } else {
        setEquipmentLoading(false);
        emitGameEvent('notification', { type: 'error', message: data.error || 'Failed to equip' });
      }
    };

    networkManager.onUnequipResult = (data) => {
      if (data.success) {
        loadInventory();
        emitGameEvent('notification', { type: 'success', message: 'Item unequipped' });
      } else {
        setEquipmentLoading(false);
        emitGameEvent('notification', { type: 'error', message: data.error || 'Failed to unequip' });
      }
    };

    networkManager.onBankTransferResult = (data) => {
      if (data.success) {
        loadInventory();
        emitGameEvent('notification', { type: 'success', message: 'Sent to bank' });
      } else {
        setEquipmentLoading(false);
        emitGameEvent('notification', { type: 'error', message: data.error || 'Failed to transfer' });
      }
    };

    return () => {
      networkManager.onInventoryData = null;
      networkManager.onEquipResult = null;
      networkManager.onUnequipResult = null;
      networkManager.onBankTransferResult = null;
    };
  }, [loadInventory]);

  // Load inventory data (needed on both tabs for equipment transfer check)
  useEffect(() => {
    loadInventory();
  }, [loadInventory]);

  // Handle ESC key to go back
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onBack();
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [onBack]);

  // Handle ship selection
  const handleSelectShip = useCallback((shipId: string) => {
    setSelectedShipId(shipId);
    setShipError(null);
  }, []);

  // Check if all equipped items can transfer to the selected ship
  const canTransferEquipment = useMemo(() => {
    if (!hasEquippedItems || !selectedShipId) return true;
    const targetShip = ships.find(s => s.id === selectedShipId);
    if (!targetShip) return false;
    const targetClass = SHIPS[targetShip.shipClassId];
    if (!targetClass) return false;

    // Collect equipped items
    const equipped = slots.filter(s => s.equipped).map(s => getEquipmentById(s.equipped));
    const usedSlots = new Set<number>();

    for (const eq of equipped) {
      if (!eq) return false;
      let found = false;
      for (let i = 0; i < targetClass.slots.length; i++) {
        if (usedSlots.has(i)) continue;
        if (canEquipInSlot(eq, targetClass.slots[i].type, targetClass.slots[i].size)) {
          usedSlots.add(i);
          found = true;
          break;
        }
      }
      if (!found) return false;
    }
    return true;
  }, [hasEquippedItems, selectedShipId, ships, slots]);

  // Check if cargo fits in the selected ship
  const cargoFitsInTarget = useMemo(() => {
    if (!selectedShipId) return true;
    const targetShip = ships.find(s => s.id === selectedShipId);
    if (!targetShip) return true;
    const targetClass = SHIPS[targetShip.shipClassId];
    if (!targetClass) return true;
    return cargoUsed <= targetClass.stats.cargoCapacity;
  }, [selectedShipId, ships, cargoUsed]);

  // Handle switch to selected ship
  const handleSwitchShip = useCallback((transferEquipment: boolean = false) => {
    if (!selectedShipId || selectedShipId === currentShipId) return;

    setSwitching(true);
    setShipError(null);
    networkManager.sendSwitchShip(selectedShipId, stationId, transferEquipment || undefined);
  }, [selectedShipId, currentShipId, stationId]);

  // Drag handlers for equipment tab
  const handleDragStart = (event: DragStartEvent) => {
    setActiveData(event.active.data.current);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveData(null);

    if (!over || equipmentLoading) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    // Dragging from inventory to equipment slot
    if (activeData?.type === 'inventory' && overData?.slotType) {
      const equipmentId = activeData.equipmentId;
      const equipment = getEquipmentById(equipmentId);

      if (equipment && equipment.slotType === overData.slotType) {
        const sizeOrder = ['SMALL', 'MEDIUM', 'LARGE'];
        const eqSizeIdx = sizeOrder.indexOf(equipment.size);
        const slotSizeIdx = sizeOrder.indexOf(overData.size);

        if (eqSizeIdx <= slotSizeIdx) {
          setEquipmentLoading(true);
          networkManager.sendEquipItem('', equipmentId, overData.slotIndex);
        } else {
          emitGameEvent('notification', { type: 'warning', message: 'Equipment too large for this slot' });
        }
      } else {
        emitGameEvent('notification', { type: 'warning', message: 'Incompatible slot type' });
      }
    }

    // Dragging from equipment slot to inventory area
    if (activeData?.type === 'equipped' && over.id === 'inventory-drop-zone') {
      setEquipmentLoading(true);
      networkManager.sendUnequipItem('', activeData.slotIndex);
    }

    // Dragging from inventory to bank
    if (activeData?.type === 'inventory' && over.id === 'bank-drop-zone') {
      const equipmentId = activeData.equipmentId;
      setEquipmentLoading(true);
      networkManager.sendShipToBank(equipmentId, 1);
    }
  };

  const canDropOnSlot = (equipmentId: string, slotType: string, slotSize: string): boolean => {
    const equipment = getEquipmentById(equipmentId);
    if (!equipment) return false;
    if (equipment.slotType !== slotType) return false;

    const sizeOrder = ['SMALL', 'MEDIUM', 'LARGE'];
    const eqSizeIdx = sizeOrder.indexOf(equipment.size);
    const slotSizeIdx = sizeOrder.indexOf(slotSize);
    return eqSizeIdx <= slotSizeIdx;
  };

  // Get current ship data
  const currentShip = ships.find(s => s.id === currentShipId);
  const currentShipClass = currentShip ? SHIPS[currentShip.shipClassId] : undefined;

  // Handle rename
  const handleStartRename = useCallback(() => {
    if (!currentShip) return;
    const currentShipClassData = SHIPS[currentShip.shipClassId];
    setRenameValue(currentShip.customName || currentShipClassData?.name || '');
    setIsRenaming(true);
  }, [currentShip]);

  const handleConfirmRename = useCallback(() => {
    const trimmed = renameValue.trim();
    if (!trimmed || !currentShipId) return;
    networkManager.sendRenameShip(currentShipId, trimmed);
  }, [renameValue, currentShipId]);

  const handleCancelRename = useCallback(() => {
    setIsRenaming(false);
  }, []);

  // Get the selected ship data
  const selectedShip = ships.find(s => s.id === selectedShipId && s.id !== currentShipId);
  const selectedShipClass = selectedShip ? SHIPS[selectedShip.shipClassId] : undefined;

  // Filter ships for the list
  const filteredShips = useMemo(() => {
    return ships.filter(ship => {
      if (ship.id === currentShipId) return false;
      if (classFilter !== 'all') {
        const shipClass = SHIPS[ship.shipClassId];
        if (shipClass?.class !== classFilter) return false;
      }
      return true;
    });
  }, [ships, currentShipId, classFilter]);

  // Count ships per class for filter badges
  const classCountsForFilter = useMemo(() => {
    const counts: Record<string, number> = { all: 0 };
    ships.forEach(ship => {
      if (ship.id === currentShipId) return;
      counts.all++;
      const shipClass = SHIPS[ship.shipClassId];
      if (shipClass) {
        counts[shipClass.class] = (counts[shipClass.class] || 0) + 1;
      }
    });
    return counts;
  }, [ships, currentShipId]);

  const canSwitch = selectedShip && !switching;
  const needsTransfer = hasEquippedItems;

  // Group slots by type for equipment tab
  const slotsByType: Record<string, InventorySlotData[]> = {};
  for (const slot of slots) {
    if (!slotsByType[slot.slotType]) {
      slotsByType[slot.slotType] = [];
    }
    slotsByType[slot.slotType].push(slot);
  }

  // Calculate AIP (Average Item Power) — sum of equipped items' power cost
  let totalPower = 0;
  for (const slot of slots) {
    if (slot.equipped) {
      const eq = getEquipmentById(slot.equipped);
      if (eq) totalPower += eq.power;
    }
  }

  // Separate cargo items
  const cargoEquipment = shipInventory.filter(i => i.itemType === 'equipment');
  const cargoResources = shipInventory.filter(i => i.itemType === 'resource');

  // Check if equipment can fit in ANY slot on the current ship
  const canFitInAnySlot = (equipmentId: string): boolean => {
    const equipment = getEquipmentById(equipmentId);
    if (!equipment) return false;
    return slots.some(slot => canDropOnSlot(equipmentId, slot.slotType, slot.size));
  };

  // Get dragged item info for overlay
  const getDraggedItemInfo = () => {
    if (!activeData) return null;
    if (activeData.type === 'equipped') {
      const slot = slots.find(s => s.slotIndex === activeData.slotIndex);
      if (slot?.equipped) return getEquipmentById(slot.equipped);
    } else if (activeData.type === 'inventory') {
      return getEquipmentById(activeData.equipmentId);
    }
    return null;
  };

  const draggedItem = getDraggedItemInfo();

  // Render Ships Tab
  const renderShipsTab = () => (
    <div style={tabContentStyle}>
      {/* Left pane - Ship list */}
      <div style={leftPaneStyle}>
        <div style={paneTitleStyle}>Available Ships ({classCountsForFilter.all})</div>

        {/* Class filters */}
        <div style={filterContainerStyle}>
          {SHIP_CLASSES.map(cls => {
            const count = classCountsForFilter[cls.id] || 0;
            if (cls.id !== 'all' && count === 0) return null;
            return (
              <button
                key={cls.id}
                style={filterButtonStyle(classFilter === cls.id)}
                onClick={() => setClassFilter(cls.id)}
              >
                {cls.label} {count > 0 ? `(${count})` : ''}
              </button>
            );
          })}
        </div>

        <div style={shipListStyle}>
          {shipsLoading ? (
            <div style={emptyStateStyle}>Loading ships...</div>
          ) : filteredShips.length === 0 ? (
            <div style={emptyStateStyle}>
              {classFilter === 'all' ? 'No other ships in your bank' : `No ${classFilter} ships available`}
            </div>
          ) : (
            filteredShips.map((ship) => {
              const sc = SHIPS[ship.shipClassId];
              const isSelected = ship.id === selectedShipId;

              return (
                <div
                  key={ship.id}
                  style={shipItemStyle(isSelected)}
                  onClick={() => handleSelectShip(ship.id)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: PALETTE.LIGHT, fontWeight: 600, fontSize: '11px' }}>
                      {getShipDisplayName(ship, sc)}
                    </span>
                  </div>
                  <div style={{ marginTop: '4px', fontSize: '11px', color: PALETTE.DIM }}>
                    <span style={{ color: getTierColor(sc?.tier || 1) }}>T{sc?.tier || '?'}</span>
                    {' '}{getClassDisplayName(sc?.class || 'unknown')}
                    {' - '}
                    <span style={{ color: ship.hull < (sc?.stats.hull || 100) * 0.5 ? PALETTE.RED : PALETTE.GREEN }}>
                      {Math.round(ship.hull)} HP
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Right pane - Selected ship info */}
      <div style={rightPaneStyle}>
        {selectedShip && selectedShipClass ? (
          <>
            {/* Selected Ship Info Box */}
            <ShipInfoBox
              ship={selectedShip}
              shipClass={selectedShipClass}
              title="Selected Ship"
              color={PALETTE.ORANGE}
            />

            {/* Comparison table (if we have current ship data) */}
            {currentShip && currentShipClass && (
              <>
                <div style={{ ...paneTitleStyle, marginTop: '8px' }}>Comparison vs Current</div>
                <div style={comparisonContainerStyle}>
                  {/* Header row */}
                  <div style={comparisonHeaderStyle}>
                    <div style={comparisonLabelColStyle}>Stat</div>
                    <div style={comparisonValueColStyle}>
                      <span style={{ color: PALETTE.TEAL }}>Current</span>
                      <span style={{ fontSize: '10px', color: PALETTE.DIM }}>{currentShipClass.name}</span>
                    </div>
                    <div style={comparisonValueColStyle}>
                      <span style={{ color: PALETTE.ORANGE }}>Selected</span>
                      <span style={{ fontSize: '10px', color: PALETTE.DIM }}>{selectedShipClass.name}</span>
                    </div>
                  </div>

                  {/* Stat rows */}
                  {renderComparisonRow('Hull', currentShip.hull, selectedShip.hull, currentShipClass.stats.hull, selectedShipClass.stats.hull, true)}
                  {renderComparisonRow('Max Hull', currentShipClass.stats.hull, selectedShipClass.stats.hull)}
                  {renderComparisonRow('Shield', currentShipClass.stats.shield, selectedShipClass.stats.shield)}
                  {renderComparisonRow('Max Speed', currentShipClass.stats.baseMaxSpeed, selectedShipClass.stats.baseMaxSpeed)}
                  {renderComparisonRow('Acceleration', currentShipClass.stats.baseAcceleration, selectedShipClass.stats.baseAcceleration)}
                  {renderComparisonRow('Rotation', currentShipClass.stats.rotationSpeed, selectedShipClass.stats.rotationSpeed, undefined, undefined, false, '°/s')}
                  {renderComparisonRow('Cargo', currentShipClass.stats.cargoCapacity, selectedShipClass.stats.cargoCapacity)}
                  {renderComparisonRow('Mass', currentShipClass.stats.mass, selectedShipClass.stats.mass, undefined, undefined, false, '', true)}
                  {renderComparisonRow('Fitting', currentShipClass.stats.powerCapacity, selectedShipClass.stats.powerCapacity)}
                  {renderComparisonRow('Slots', currentShipClass.slots.length, selectedShipClass.slots.length)}
                </div>
              </>
            )}

            {/* Switch actions */}
            <div style={switchActionsStyle}>
              {shipError && (
                <div style={errorMessageStyle}>{shipError}</div>
              )}

              {!cargoFitsInTarget && (
                <div style={warningMessageStyle}>
                  New ship has {selectedShipClass?.stats.cargoCapacity} cargo capacity but you have {cargoUsed} cargo. Transfer or discard items first.
                </div>
              )}

              {needsTransfer && (
                <div style={infoMessageStyle}>
                  {equippedCount} equipped item{equippedCount > 1 ? 's' : ''} will stay on your current ship.
                </div>
              )}

              {needsTransfer && canTransferEquipment && (
                <button
                  style={switchButtonStyle(!canSwitch || !cargoFitsInTarget)}
                  disabled={!canSwitch || !cargoFitsInTarget}
                  onClick={() => handleSwitchShip(true)}
                >
                  {switching ? 'Switching...' : 'Transfer Equipment & Switch'}
                </button>
              )}

              <button
                style={switchButtonStyle(!canSwitch || !cargoFitsInTarget)}
                disabled={!canSwitch || !cargoFitsInTarget}
                onClick={() => handleSwitchShip(false)}
              >
                {switching ? 'Switching...' : 'Switch to This Ship'}
              </button>
            </div>
          </>
        ) : (
          <div style={{ ...rightPaneStyle, justifyContent: 'center', alignItems: 'center' }}>
            <div style={emptyStateStyle}>
              {filteredShips.length === 0
                ? 'No ships available to select'
                : 'Select a ship from the list to compare'}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Helper to render comparison row
  const renderComparisonRow = (
    label: string,
    currentVal: number,
    selectedVal: number,
    currentMax?: number,
    selectedMax?: number,
    showAsHp?: boolean,
    suffix?: string,
    lowerIsBetter?: boolean
  ) => {
    const diff = selectedVal - currentVal;
    const isBetter = lowerIsBetter ? diff < 0 : diff > 0;
    const diffColor = diff === 0 ? PALETTE.DIM : isBetter ? PALETTE.GREEN : PALETTE.RED;
    const diffSign = diff > 0 ? '+' : '';

    return (
      <div style={comparisonRowStyle}>
        <div style={comparisonLabelColStyle}>{label}</div>
        <div style={comparisonValueColStyle}>
          {showAsHp && currentMax ? (
            <span>
              <span style={{ color: currentVal < currentMax * 0.5 ? PALETTE.RED : PALETTE.LIGHT }}>
                {Math.round(currentVal)}
              </span>
              <span style={{ color: PALETTE.DIM }}>/{currentMax}</span>
            </span>
          ) : (
            <span>{Math.round(currentVal)}{suffix}</span>
          )}
        </div>
        <div style={comparisonValueColStyle}>
          {showAsHp && selectedMax ? (
            <span>
              <span style={{ color: selectedVal < selectedMax * 0.5 ? PALETTE.RED : PALETTE.LIGHT }}>
                {Math.round(selectedVal)}
              </span>
              <span style={{ color: PALETTE.DIM }}>/{selectedMax}</span>
            </span>
          ) : (
            <span>{Math.round(selectedVal)}{suffix}</span>
          )}
          {diff !== 0 && (
            <span style={{ color: diffColor, fontSize: '11px', marginLeft: '6px' }}>
              ({diffSign}{Math.round(diff)})
            </span>
          )}
        </div>
      </div>
    );
  };

  // Render Equipment Tab
  const renderEquipmentTab = () => (
    <DndContext
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      collisionDetection={pointerWithin}
    >
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
        {/* Current Ship Info Row with Rename */}
        {currentShip && currentShipClass && (
          <div style={{ padding: '12px', borderBottom: `1px solid ${PALETTE.BORDER}` }}>
            <div
              style={{
                display: 'flex',
                gap: '16px',
                padding: '12px',
                backgroundColor: PALETTE.PANEL,
                border: `1px solid ${PALETTE.BORDER}`,
                borderRadius: '4px',
                marginBottom: '12px',
              }}
            >
              <ShipPreview
                shipClassId={currentShipClass.id}
                size={currentShipClass.size}
                color={PALETTE.TEAL}
                previewSize={100}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: PALETTE.DIM, fontSize: '9px', marginBottom: '4px', textTransform: 'uppercase' }}>
                  Current Ship
                </div>
                {/* Ship name with rename */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  {isRenaming ? (
                    <>
                      <input
                        type="text"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleConfirmRename();
                          if (e.key === 'Escape') handleCancelRename();
                          e.stopPropagation();
                        }}
                        maxLength={32}
                        autoFocus
                        style={{
                          backgroundColor: PALETTE.DARK,
                          border: `1px solid ${PALETTE.TEAL}`,
                          borderRadius: '3px',
                          color: PALETTE.LIGHT,
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: '11px',
                          fontWeight: 600,
                          padding: '2px 8px',
                          outline: 'none',
                          flex: 1,
                          maxWidth: '300px',
                        }}
                      />
                      <button
                        onClick={handleConfirmRename}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: PALETTE.GREEN,
                          cursor: 'pointer',
                          padding: '4px',
                          display: 'flex',
                        }}
                        title="Confirm"
                      >
                        <FiCheck size={16} />
                      </button>
                      <button
                        onClick={handleCancelRename}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: PALETTE.RED,
                          cursor: 'pointer',
                          padding: '4px',
                          display: 'flex',
                        }}
                        title="Cancel"
                      >
                        <FiX size={16} />
                      </button>
                    </>
                  ) : (
                    <>
                      <span style={{ color: PALETTE.LIGHT, fontWeight: 600, fontSize: '11px' }}>
                        {currentShip.customName || currentShipClass.name}
                      </span>
                      <button
                        onClick={handleStartRename}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: PALETTE.DIM,
                          cursor: 'pointer',
                          padding: '4px',
                          display: 'flex',
                          transition: 'color 0.15s',
                        }}
                        title="Rename ship"
                        onMouseEnter={(e) => (e.currentTarget.style.color = PALETTE.TEAL)}
                        onMouseLeave={(e) => (e.currentTarget.style.color = PALETTE.DIM)}
                      >
                        <FiEdit2 size={14} />
                      </button>
                    </>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '11px', color: PALETTE.DIM }}>
                  <span>
                    <span style={{ color: getTierColor(currentShipClass.tier) }}>T{currentShipClass.tier}</span>
                    {' '}{getClassDisplayName(currentShipClass.class)}
                  </span>
                  <span>
                    Size: <span style={{ color: PALETTE.LIGHT }}>{currentShipClass.size.charAt(0).toUpperCase() + currentShipClass.size.slice(1)}</span>
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '11px', color: PALETTE.DIM, marginTop: '4px' }}>
                  <span>
                    Hull: <span style={{ color: currentShip.hull < currentShipClass.stats.hull * 0.3 ? PALETTE.RED : PALETTE.GREEN }}>
                      {Math.round(currentShip.hull)}/{currentShipClass.stats.hull}
                    </span>
                  </span>
                  <span>
                    Cargo: <span style={{ color: PALETTE.LIGHT }}>{currentShipClass.stats.cargoCapacity}</span>
                  </span>
                  <span>
                    Slots: <span style={{ color: PALETTE.LIGHT }}>{currentShipClass.slots.length}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main content area */}
        <div style={tabContentStyle}>
          {/* Loading overlay */}
          {equipmentLoading && (
            <div style={loadingOverlayStyle}>
              <div style={loadingBoxStyle}>Processing...</div>
            </div>
          )}

          {/* Left pane: Equipment Slots */}
          <div style={equipmentLeftPaneStyle}>
            <div style={paneTitleStyle}>
              Equipment Slots
              <span style={powerBadgeStyle}>AIP: {totalPower}</span>
            </div>
          <div style={slotsContainerStyle}>
            {Object.entries(slotsByType).map(([slotType, typeSlots]) => (
              <div key={slotType} style={slotSectionStyle}>
                <div style={slotLabelStyle(SLOT_COLORS[slotType] || PALETTE.DIM)}>
                  {slotType}
                </div>
                {typeSlots.map((slot) => {
                  const equipment = slot.equipped ? getEquipmentById(slot.equipped) : null;
                  const canDrop = activeData?.type === 'inventory' &&
                    canDropOnSlot(activeData.equipmentId, slot.slotType, slot.size);

                  return (
                    <DroppableSlot
                      key={slot.slotIndex}
                      id={`slot-${slot.slotIndex}`}
                      data={{ slotType: slot.slotType, slotIndex: slot.slotIndex, size: slot.size }}
                      canDrop={canDrop}
                    >
                      <div style={slotRowStyle}>
                        {equipment ? (
                          <DraggableItem
                            id={`equipped-${slot.slotIndex}`}
                            data={{ type: 'equipped', slotIndex: slot.slotIndex }}
                            disabled={equipmentLoading}
                          >
                            <div style={equippedItemStyle}>
                              <div style={equipmentInfoStyle}>
                                <span style={equipmentNameStyle}>{equipment.name}</span>
                                <span style={equipmentTierStyle}>T{equipment.tier}</span>
                              </div>
                              <span style={dragHintStyle}>drag to unequip</span>
                            </div>
                          </DraggableItem>
                        ) : (
                          <div style={emptySlotStyle}>
                            <span style={activeData?.type === 'inventory'
                              ? (canDrop ? compatibleTextStyle : notCompatibleTextStyle)
                              : emptySlotTextStyle
                            }>
                              {activeData?.type === 'inventory'
                                ? (canDrop ? 'COMPATIBLE' : 'NOT COMPATIBLE')
                                : `Empty ${slot.size.toLowerCase()}`
                              }
                            </span>
                          </div>
                        )}
                      </div>
                    </DroppableSlot>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div style={dividerStyle} />

        {/* Right pane: Inventory */}
        <InventoryDropZone id="inventory-drop-zone">
          <div style={inventoryPaneStyle}>
            <div style={paneTitleStyle}>
              Ship Cargo
              <span style={cargoIndicatorStyle(cargoUsed, cargoCapacity)}>
                {cargoUsed}/{cargoCapacity}
              </span>
            </div>

            {/* Equipment in cargo */}
            {cargoEquipment.length > 0 && (
              <div style={inventorySectionStyle}>
                <div style={inventorySectionTitleStyle}>EQUIPMENT</div>
                <div style={inventoryGridStyle}>
                  {cargoEquipment.map((item) => {
                    const equipment = getEquipmentById(item.itemId);
                    if (!equipment) return null;
                    const color = SLOT_COLORS[equipment.slotType] || PALETTE.DIM;
                    const canFit = canFitInAnySlot(item.itemId);

                    return (
                      <DraggableItem
                        key={item.itemId}
                        id={`inventory-${item.itemId}`}
                        data={{ type: 'inventory', equipmentId: item.itemId }}
                        disabled={equipmentLoading}
                      >
                        <div style={canFit ? inventoryItemStyle(color) : incompatibleItemStyle(color)}>
                          <div style={inventoryItemInfoStyle}>
                            <span style={canFit ? inventoryItemNameStyle : incompatibleItemNameStyle}>{equipment.name}</span>
                            <span style={inventoryItemTypeStyle(color)}>
                              {equipment.slotType} T{equipment.tier}
                            </span>
                            <span style={volumeBadgeStyle}>
                              {(equipment.volume || 1) * item.quantity} vol
                            </span>
                            {item.quantity > 1 && (
                              <span style={quantityBadgeStyle}>x{item.quantity}</span>
                            )}
                            {!canFit && (
                              <span style={noFitBadgeStyle}>NO FIT</span>
                            )}
                          </div>
                        </div>
                      </DraggableItem>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Resources */}
            {cargoResources.length > 0 && (
              <div style={inventorySectionStyle}>
                <div style={inventorySectionTitleStyle}>RESOURCES</div>
                <div style={resourceGridStyle}>
                  {cargoResources.map((item) => {
                    const resourceId = item.itemId.replace('resource_', '');
                    const resourceInfo = RESOURCES[resourceId.toUpperCase() as keyof typeof RESOURCES];
                    const color = RESOURCE_COLORS[resourceId] || PALETTE.TEAL;

                    return (
                      <div key={item.itemId} style={resourceItemStyle(color)}>
                        <span style={resourceNameStyle}>
                          {resourceInfo?.name || resourceId}
                        </span>
                        <span style={volumeBadgeStyle}>{item.quantity} vol</span>
                        <span style={resourceQuantityStyle(color)}>x{item.quantity}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {cargoEquipment.length === 0 && cargoResources.length === 0 && (
              <div style={emptyCargoStyle}>
                {activeData?.type === 'equipped'
                  ? 'Drop here to unequip'
                  : 'Cargo hold is empty'}
              </div>
            )}

            {/* Bank drop zone - appears when dragging cargo items */}
            <div style={{ marginTop: 'auto' }}>
              <BankDropZone id="bank-drop-zone" isDragging={activeData?.type === 'inventory'} />
            </div>
          </div>
        </InventoryDropZone>
      </div>
      </div>

      {/* Drag overlay */}
      <DragOverlay>
        {draggedItem && (
          <div style={dragOverlayStyle(SLOT_COLORS[draggedItem.slotType])}>
            <span>{draggedItem.name}</span>
            <span style={dragOverlayTierStyle}>T{draggedItem.tier}</span>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <button style={backButtonStyle} onClick={onBack}>
          <FiArrowLeft size={16} />
          <span>BACK</span>
        </button>
        <div style={headerTabsStyle}>
          <button
            style={headerTabStyle(activeTab === 'equipment')}
            onClick={() => setActiveTab('equipment')}
          >
            Current Ship
          </button>
          <button
            style={headerTabStyle(activeTab === 'ships')}
            onClick={() => setActiveTab('ships')}
          >
            Ships
          </button>
        </div>
        <span style={hintStyle}>[ESC] Return to Station</span>
      </div>

      {/* Tab Content */}
      {activeTab === 'ships' ? renderShipsTab() : renderEquipmentTab()}

      {/* Footer */}
      <div style={footerStyle}>
        {activeTab === 'ships' ? (
          <span>Select a ship to compare stats and switch</span>
        ) : (
          <span>Drag equipment between slots and cargo</span>
        )}
      </div>
    </div>
  );
}

// ==================== STYLES ====================

const containerStyle = stationContainerStyle;
const headerStyle = stationHeaderStyle;
const backButtonStyle = stationBackButtonStyle;
const headerTabsStyle = stationHeaderTabsStyle;
const headerTabStyle = stationHeaderTabStyle;
const hintStyle = stationHintStyle;

const tabContentStyle: React.CSSProperties = {
  display: 'flex',
  flex: 1,
  overflow: 'hidden',
  position: 'relative',
};

const leftPaneStyle: React.CSSProperties = {
  width: '320px',
  display: 'flex',
  flexDirection: 'column',
  borderRight: `1px solid ${PALETTE.BORDER}`,
  overflow: 'hidden',
};

const rightPaneStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  padding: '16px',
};

const paneTitleStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontSize: '11px',
  fontWeight: 600,
  color: PALETTE.DIM,
  borderBottom: `1px solid ${PALETTE.BORDER}`,
  textTransform: 'uppercase',
  letterSpacing: '1px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const filterContainerStyle: React.CSSProperties = {
  padding: '8px',
  borderBottom: `1px solid ${PALETTE.BORDER}`,
  display: 'flex',
  flexWrap: 'wrap',
  gap: '4px',
};

const filterButtonStyle = (isActive: boolean): React.CSSProperties => ({
  padding: '4px 8px',
  fontSize: '11px',
  backgroundColor: isActive ? PALETTE.TEAL : 'transparent',
  color: isActive ? PALETTE.DARK : PALETTE.DIM,
  border: `1px solid ${isActive ? PALETTE.TEAL : PALETTE.DIM}`,
  borderRadius: '3px',
  cursor: 'pointer',
  transition: 'all 0.15s ease',
});

const shipListStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '8px',
};

const shipItemStyle = (isSelected: boolean): React.CSSProperties => ({
  padding: '12px',
  marginBottom: '8px',
  backgroundColor: isSelected ? PALETTE.PANEL : 'transparent',
  border: `2px solid ${isSelected ? PALETTE.TEAL : 'transparent'}`,
  borderRadius: '6px',
  cursor: 'pointer',
  transition: 'all 0.15s ease',
});

const emptyStateStyle: React.CSSProperties = {
  padding: '20px',
  textAlign: 'center',
  color: PALETTE.DIM,
  fontSize: '11px',
};

// Comparison styles
const comparisonContainerStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
};

const comparisonHeaderStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '120px 1fr 1fr',
  padding: '8px 12px',
  backgroundColor: PALETTE.PANEL,
  borderRadius: '4px',
  marginBottom: '8px',
  fontWeight: 600,
  fontSize: '11px',
};

const comparisonRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '120px 1fr 1fr',
  padding: '6px 12px',
  borderBottom: `1px solid ${PALETTE.BORDER}`,
  fontSize: '11px',
};

const comparisonLabelColStyle: React.CSSProperties = {
  color: PALETTE.DIM,
};

const comparisonValueColStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  color: PALETTE.LIGHT,
};

const switchActionsStyle: React.CSSProperties = {
  marginTop: 'auto',
  paddingTop: '16px',
  borderTop: `1px solid ${PALETTE.BORDER}`,
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
};

const errorMessageStyle: React.CSSProperties = {
  padding: '10px 12px',
  backgroundColor: `${PALETTE.RED}22`,
  border: `1px solid ${PALETTE.RED}`,
  borderRadius: '4px',
  color: PALETTE.RED,
  fontSize: '11px',
};

const warningMessageStyle: React.CSSProperties = {
  padding: '10px 12px',
  backgroundColor: `${PALETTE.ORANGE}22`,
  border: `1px solid ${PALETTE.ORANGE}`,
  borderRadius: '4px',
  color: PALETTE.ORANGE,
  fontSize: '11px',
};

const infoMessageStyle: React.CSSProperties = {
  padding: '10px 12px',
  backgroundColor: `${PALETTE.TEAL}22`,
  border: `1px solid ${PALETTE.TEAL}`,
  borderRadius: '4px',
  color: PALETTE.TEAL,
  fontSize: '11px',
};

const switchButtonStyle = (disabled: boolean): React.CSSProperties => ({
  padding: '12px 20px',
  fontSize: '11px',
  fontWeight: 600,
  backgroundColor: disabled ? PALETTE.DIM : PALETTE.TEAL,
  color: disabled ? PALETTE.LIGHT : PALETTE.DARK,
  border: 'none',
  borderRadius: '4px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.6 : 1,
  transition: 'all 0.15s',
});

// Equipment tab styles
const equipmentLeftPaneStyle: React.CSSProperties = {
  width: '380px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const powerBadgeStyle: React.CSSProperties = {
  fontSize: '11px',
  color: PALETTE.YELLOW,
  fontWeight: 600,
};

const slotsContainerStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '12px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
};

const slotSectionStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
};

const slotLabelStyle = (color: string): React.CSSProperties => ({
  fontSize: '10px',
  color: color,
  textTransform: 'uppercase',
  letterSpacing: '1px',
  marginBottom: '2px',
  paddingLeft: '2px',
});

const slotRowStyle: React.CSSProperties = {
  padding: '2px',
  borderRadius: '4px',
  transition: 'all 0.15s ease',
};

const equippedItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px 12px',
  backgroundColor: PALETTE.PANEL,
  border: `1px solid ${PALETTE.TEAL}66`,
  borderRadius: '4px',
  cursor: 'grab',
};

const equipmentInfoStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
};

const equipmentNameStyle: React.CSSProperties = {
  fontSize: '11px',
  color: PALETTE.LIGHT,
  fontWeight: 600,
};

const equipmentTierStyle: React.CSSProperties = {
  fontSize: '10px',
  color: PALETTE.DIM,
  backgroundColor: PALETTE.DARK,
  padding: '2px 6px',
  borderRadius: '3px',
};

const emptySlotStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '12px',
  backgroundColor: `${PALETTE.PANEL}88`,
  border: `1px dashed ${PALETTE.DIM}66`,
  borderRadius: '4px',
};

const emptySlotTextStyle: React.CSSProperties = {
  fontSize: '11px',
  color: PALETTE.DIM,
  textTransform: 'uppercase',
};

const compatibleTextStyle: React.CSSProperties = {
  fontSize: '11px',
  color: PALETTE.GREEN,
  textTransform: 'uppercase',
  fontWeight: 600,
};

const notCompatibleTextStyle: React.CSSProperties = {
  fontSize: '11px',
  color: PALETTE.RED,
  textTransform: 'uppercase',
  opacity: 0.7,
};

const dragHintStyle: React.CSSProperties = {
  fontSize: '10px',
  color: PALETTE.DIM,
  fontStyle: 'italic',
  opacity: 0.7,
};

const dividerStyle: React.CSSProperties = {
  width: '1px',
  backgroundColor: PALETTE.BORDER,
};

const inventoryPaneStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const cargoIndicatorStyle = (used: number, capacity: number): React.CSSProperties => ({
  padding: '3px 10px',
  borderRadius: '4px',
  fontSize: '11px',
  fontWeight: 600,
  backgroundColor: used >= capacity ? `${PALETTE.RED}33` : `${PALETTE.TEAL}22`,
  color: used >= capacity ? PALETTE.RED : PALETTE.TEAL,
  border: `1px solid ${used >= capacity ? PALETTE.RED : PALETTE.TEAL}44`,
});

const inventorySectionStyle: React.CSSProperties = {
  padding: '12px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const inventorySectionTitleStyle: React.CSSProperties = {
  fontSize: '11px',
  color: PALETTE.DIM,
  textTransform: 'uppercase',
  letterSpacing: '1px',
};

const inventoryGridStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
};

const inventoryItemStyle = (color: string): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px 12px',
  backgroundColor: PALETTE.PANEL,
  border: `1px solid ${color}44`,
  borderRadius: '4px',
  cursor: 'grab',
  transition: 'all 0.15s ease',
});

const incompatibleItemStyle = (_color: string): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px 12px',
  backgroundColor: `${PALETTE.RED}08`,
  border: `1px solid ${PALETTE.RED}44`,
  borderRadius: '4px',
  cursor: 'not-allowed',
  transition: 'all 0.15s ease',
  opacity: 0.6,
});

const inventoryItemInfoStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  flex: 1,
};

const inventoryItemNameStyle: React.CSSProperties = {
  fontSize: '11px',
  color: PALETTE.LIGHT,
  fontWeight: 600,
};

const incompatibleItemNameStyle: React.CSSProperties = {
  fontSize: '11px',
  color: PALETTE.DIM,
  fontWeight: 600,
};

const inventoryItemTypeStyle = (color: string): React.CSSProperties => ({
  fontSize: '10px',
  color: color,
  textTransform: 'uppercase',
});

const volumeBadgeStyle: React.CSSProperties = {
  fontSize: '10px',
  color: PALETTE.TEAL,
  backgroundColor: `${PALETTE.TEAL}15`,
  border: `1px solid ${PALETTE.TEAL}33`,
  padding: '1px 5px',
  borderRadius: '3px',
  letterSpacing: '0.3px',
};

const quantityBadgeStyle: React.CSSProperties = {
  fontSize: '11px',
  color: PALETTE.DIM,
  backgroundColor: PALETTE.DARK,
  padding: '2px 6px',
  borderRadius: '3px',
};

const noFitBadgeStyle: React.CSSProperties = {
  fontSize: '9px',
  color: PALETTE.RED,
  backgroundColor: `${PALETTE.RED}18`,
  border: `1px solid ${PALETTE.RED}44`,
  padding: '2px 5px',
  borderRadius: '3px',
  letterSpacing: '0.5px',
  fontWeight: 600,
};

const resourceGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
  gap: '8px',
};

const resourceItemStyle = (color: string): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 12px',
  backgroundColor: PALETTE.PANEL,
  border: `1px solid ${color}33`,
  borderRadius: '4px',
});

const resourceNameStyle: React.CSSProperties = {
  fontSize: '11px',
  color: PALETTE.LIGHT,
  textTransform: 'capitalize',
};

const resourceQuantityStyle = (color: string): React.CSSProperties => ({
  fontSize: '11px',
  fontWeight: 600,
  color: color,
});

const emptyCargoStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '60px 20px',
  color: PALETTE.DIM,
  fontSize: '11px',
  border: `2px dashed ${PALETTE.DIM}44`,
  borderRadius: '8px',
  margin: '16px',
  flex: 1,
};

const loadingOverlayStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(15, 27, 38, 0.8)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 20,
};

const loadingBoxStyle: React.CSSProperties = {
  padding: '20px 40px',
  backgroundColor: PALETTE.PANEL,
  borderRadius: '8px',
  border: `1px solid ${PALETTE.TEAL}`,
  color: PALETTE.TEAL,
  fontSize: '11px',
};

const dragOverlayStyle = (color: string): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 16px',
  backgroundColor: PALETTE.PANEL,
  border: `2px solid ${color}`,
  borderRadius: '6px',
  boxShadow: `0 4px 20px ${color}44`,
  cursor: 'grabbing',
  fontFamily: "'JetBrains Mono', monospace",
  color: PALETTE.LIGHT,
  fontSize: '11px',
  fontWeight: 600,
});

const dragOverlayTierStyle: React.CSSProperties = {
  fontSize: '10px',
  color: PALETTE.DIM,
  backgroundColor: PALETTE.DARK,
  padding: '2px 6px',
  borderRadius: '3px',
};

const footerStyle = stationFooterStyle;

export default Shipyard;
